import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Save, Loader2 } from "lucide-react";

const STATUS_MAP = {
  new: { label: "Nuovo" },
  contacted: { label: "Contattato" },
  qualified: { label: "Qualificato" },
  proposal_sent: { label: "Proposta Inviata" },
  won: { label: "Vinto" },
  lost: { label: "Perso" },
};

const PROPERTY_MAP = {
  casa: "Casa Indipendente",
  villetta: "Villetta a Schiera",
  condominio: "Condominio",
  azienda: "Azienda",
};

const ROOF_MAP = {
  tegole: "Tegole",
  coppi: "Coppi",
  piano: "Piano / Terrazza",
  lamiera: "Lamiera",
};

const LeadDetailModal = ({ lead, onClose, onUpdate, onDelete }) => {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(lead.id, { status, notes });
      toast.success("Lead aggiornato");
      onClose();
    } catch {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Sei sicuro di voler eliminare questo lead?")) {
      await onDelete(lead.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="lead-detail-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-fade-in-up overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="lead-detail-name">
            {lead.name} {lead.surname}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100" data-testid="lead-detail-close">×</button>
        </div>
        <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-slate-400 block">Email</span>
              <span className="text-sm font-medium text-slate-900">{lead.email}</span>
            </div>
            <div>
              <span className="text-xs text-slate-400 block">Telefono</span>
              <span className="text-sm font-medium text-slate-900">{lead.phone}</span>
              {lead.phone_verified && <span className="ml-1 text-xs text-emerald-600 font-semibold">Verificato</span>}
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-bold text-slate-700">Configurazione</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-xs text-slate-400 block">Immobile</span>
                <span className="font-medium text-slate-900">{PROPERTY_MAP[lead.property_type] || lead.property_type}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block">Tetto</span>
                <span className="font-medium text-slate-900">{ROOF_MAP[lead.roof_type] || lead.roof_type}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block">Consumo Mensile</span>
                <span className="font-medium text-slate-900">€{lead.monthly_consumption}/mese</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block">Zona</span>
                <span className="font-medium text-slate-900">{lead.province}, {lead.region}</span>
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Stato</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="flex h-10 w-full rounded-xl border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="lead-status-select"
            >
              {Object.entries(STATUS_MAP).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Note</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="flex w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              placeholder="Aggiungi note..."
              data-testid="lead-notes-textarea"
            />
          </div>
          <div className="text-xs text-slate-400 space-y-1">
            <div>Creato il: {lead.created_at}</div>
            {lead.ip && <div>IP: {lead.ip}</div>}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={handleDelete}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
            data-testid="lead-delete-button"
          >
            <Trash2 className="w-4 h-4" /> Elimina
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale flex items-center gap-2 disabled:opacity-50"
            data-testid="lead-save-button"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
